import {format} from 'date-fns';
import {combine, merge, sample} from 'effector';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {DATE_REQUEST_PATTERN} from '@consts/date';
import {dateModel as date} from '@entities/date/model';
import {InvoicePageResponse, InvoiceRequestParams, InvoiceType} from '@entities/invoices/types';
import {userModel} from '@entities/user/model';
import {createGridTableModel, initFiltersTable, initSortingsTable} from '@shared/composables/table';

import {mapOrders} from '@utils/grid';

import * as api from './api';

const domain = rootDomain.createDomain('InvoicesList');

const fetchDataFx = domain.createEffect({handler: api.fetch});
fetchDataFx.failData.watch(showRestError);

const generateTinFx = domain.createEffect({
    handler: api.generateIrn,
});

const grid = createGridTableModel({domain});

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    Gate: InvoicesGate,
    tableStateChanged,
    stateReset,
} = grid;

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const data$ = domain.createStore<InvoicePageResponse | null>(null);

const searchChanged = domain.createEvent<string>();
const search$ = domain.createStore('').on(searchChanged, (_, value) => value);

const typeChanged = domain.createEvent<InvoiceType>();
const type$ = domain.createStore<InvoiceType>('INCOMING').on(typeChanged, (_, value) => value);

export const gridTokensCache$ = domain.createStore<Record<number, string | null>>({0: null});
export const gridCacheChanged = domain.createEvent<{pageNumber: number; token: string | null}>();
export const gridCacheReset = domain.createEvent();

pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, typeChanged);
pageSize$.reset(stateReset);

data$.on(fetchDataFx.doneData, (_, data) => data).reset(InvoicesGate.close);

gridTokensCache$
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

sample({
    clock: fetchDataFx.doneData,
    source: pageNumber$,
    fn: (pageNumber, result) => ({
        pageNumber: pageNumber + 1,
        token: result.nextPageRef ?? null,
    }),
    target: gridCacheChanged,
});

sample({
    clock: merge([
        filtersChanged,
        sortingsChanged,
        searchChanged,
        typeChanged,
        date.changed,
        stateReset,
    ]),
    target: gridCacheReset,
});

sample({
    clock: [InvoicesGate.state],
    filter: (newType, currentType) => currentType.type && newType !== currentType.type,
    source: type$,
    target: typeChanged,
});

sample({
    clock: merge([tableStateChanged, searchChanged, typeChanged, date.changed]),
    source: combine({
        taxNumber: userModel.state.map((state) => state.profile?.company.tin ?? ''),
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        order: sortings$,
        date: date.state,
        search: search$,
        direction: type$,
        cache: gridTokensCache$,
        role: userModel.role,
        taxAuthorityDepartment: userModel.state.map(
            (state) => Number(state.profile?.company.department?.id) ?? null,
        ),
    }),
    filter: InvoicesGate.status,
    fn: ({
        pageNumber,
        pageSize,
        order,
        search,
        direction,
        date,
        taxNumber,
        cache,
        role,
        taxAuthorityDepartment,
    }): InvoiceRequestParams => {
        const shouldSendDirection = role === 'DIRECTOR' || role === 'EMPLOYEE';
        return {
            paging: {pageSize, ...(cache?.[pageNumber] && {pageRef: cache[pageNumber]})},
            ...(taxNumber && {taxNumber}),
            ...(taxAuthorityDepartment &&
                (role === 'INSPECTOR' || role === 'CHIEF_INSPECTOR') && {taxAuthorityDepartment}),
            ...(shouldSendDirection && {direction}),
            dateFrom: date.range.from ? format(date.range.from, DATE_REQUEST_PATTERN) : '',
            dateTo: date.range.to ? format(date.range.to, DATE_REQUEST_PATTERN) : '',
            ...(search && {search}),
            ...(Object.keys(order).length && {order: mapOrders(order)}),
        };
    },
    target: fetchDataFx,
});

const $invoicesState = combine({
    data: data$,
    type: type$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    search: search$,
    pending: fetchDataFx.pending,
    gridTokensCache: gridTokensCache$,
});

export const invoicesModel = {
    state: $invoicesState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    pageNumberChanged,
    pageSizeChanged,
    searchChanged,
    typeChanged,
    generateTinFx,
};
